export interface ChatUICallbacks {
  onSend(text: string): void;
  onOpenChange?(open: boolean): void;
}

const PANEL =
  'position:absolute;left:12px;bottom:150px;width:300px;display:flex;flex-direction:column;' +
  'gap:6px;pointer-events:auto;font-family:system-ui,sans-serif;z-index:3;' +
  'background:rgba(10,10,15,0.82);border:1px solid rgba(93,255,212,0.35);' +
  'border-radius:12px;padding:8px;';
const HEADER =
  'display:flex;align-items:center;justify-content:space-between;font-size:11px;' +
  'letter-spacing:2px;color:#9fffe9;cursor:default;';
const HEADER_BTN =
  'padding:2px 8px;font-size:13px;font-weight:700;color:#e8e8ef;background:rgba(255,255,255,0.08);' +
  'border:none;border-radius:6px;cursor:pointer;';
const PILL =
  'position:absolute;left:12px;bottom:150px;padding:7px 14px;font-size:12px;font-weight:700;' +
  'color:#5dffd4;background:rgba(10,10,15,0.85);border:1px solid rgba(93,255,212,0.5);' +
  'border-radius:10px;cursor:pointer;pointer-events:auto;z-index:3;letter-spacing:1px;';
const FEED =
  'position:absolute;left:12px;bottom:200px;max-width:300px;display:flex;flex-direction:column;' +
  'gap:3px;pointer-events:none;font-family:system-ui,sans-serif;font-size:12px;line-height:1.5;' +
  'z-index:3;background:rgba(10,10,15,0.55);border-radius:8px;padding:6px 8px;';
const FEED_LINE = 'text-shadow:0 1px 2px rgba(0,0,0,0.8);overflow-wrap:anywhere;';
const FEED_MAX = 5;

/**
 * Overlay chat multiplayer yang BISA diminimalkan:
 * - Expanded: header (judul + tombol —) + log + input.
 * - Minimized: pil "CHAT (N)" + feed pasif 5 pesan terakhir (read-only,
 *   pointer-events:none) sehingga histori terlihat tanpa klik manual.
 * - Enter di input kirim (panel tetap terbuka), Escape minimalkan.
 */
export class ChatUI {
  private panel: HTMLDivElement;
  private header: HTMLDivElement;
  private log: HTMLDivElement;
  private row: HTMLDivElement;
  private input: HTMLInputElement;
  private pill: HTMLButtonElement;
  private minimizeBtn: HTMLButtonElement;
  private feed: HTMLDivElement;
  private expanded = true;
  private unread = 0;

  constructor(
    private container: HTMLElement,
    startMinimized: boolean,
    private cb: ChatUICallbacks,
  ) {
    this.panel = document.createElement('div');
    this.panel.style.cssText = PANEL;

    this.header = document.createElement('div');
    this.header.style.cssText = HEADER;
    const title = document.createElement('span');
    title.textContent = 'CHAT';
    this.header.appendChild(title);
    this.minimizeBtn = document.createElement('button');
    this.minimizeBtn.textContent = '—';
    this.minimizeBtn.style.cssText = HEADER_BTN;
    this.minimizeBtn.title = 'Minimalkan (Esc)';
    this.minimizeBtn.onclick = () => this.setExpanded(false);
    this.header.appendChild(this.minimizeBtn);
    this.panel.appendChild(this.header);

    this.log = document.createElement('div');
    this.log.style.cssText =
      'max-height:140px;overflow-y:auto;font-size:12px;line-height:1.5;' +
      'display:flex;flex-direction:column;';
    this.panel.appendChild(this.log);

    this.row = document.createElement('div');
    this.row.style.cssText = 'display:flex;gap:6px;';
    this.input = document.createElement('input');
    this.input.maxLength = 160;
    this.input.placeholder = 'Ketik pesan…';
    this.input.style.cssText =
      'flex:1;padding:6px 10px;font-size:13px;color:#e8e8ef;' +
      'background:rgba(0,0,0,0.4);border:1px solid rgba(93,255,212,0.5);border-radius:8px;outline:none;';
    const send = document.createElement('button');
    send.textContent = 'KIRIM';
    send.style.cssText =
      'padding:6px 12px;font-size:12px;font-weight:700;color:#0a0a0f;' +
      'background:#5dffd4;border:none;border-radius:8px;cursor:pointer;';
    send.onclick = () => this.submit();
    this.row.appendChild(this.input);
    this.row.appendChild(send);
    this.panel.appendChild(this.row);

    this.pill = document.createElement('button');
    this.pill.style.cssText = PILL;
    this.pill.onclick = () => this.setExpanded(true);

    this.feed = document.createElement('div');
    this.feed.style.cssText = FEED;

    // Status awal dipasang langsung TANPA emit onOpenChange: emit saat
    // konstruksi memicu resume()/requestPointerLock tanpa user-gesture
    // (pageerror) di MainGameScene.
    this.expanded = !startMinimized;
    if (this.expanded) {
      this.container.appendChild(this.panel);
    } else {
      this.container.appendChild(this.feed);
      this.container.appendChild(this.pill);
    }
    this.renderPill();
  }

  private setExpanded(expanded: boolean): void {
    if (expanded === this.expanded) {
      if (expanded) this.input.focus();
      return;
    }
    this.expanded = expanded;
    if (expanded) {
      this.pill.remove();
      this.feed.remove();
      this.container.appendChild(this.panel);
      this.unread = 0;
      this.renderPill();
      this.input.focus();
    } else {
      this.panel.remove();
      this.container.appendChild(this.feed);
      this.container.appendChild(this.pill);
      this.renderPill();
    }
    this.cb.onOpenChange?.(expanded);
  }

  private renderPill(): void {
    this.pill.textContent = this.unread > 0 ? `CHAT (${this.unread})` : 'CHAT';
  }

  private submit(): void {
    const text = this.input.value.trim();
    if (!text) return;
    this.cb.onSend(text);
    this.input.value = '';
  }

  append(name: string, text: string): void {
    const line = document.createElement('div');
    line.style.cssText = 'text-shadow:0 1px 2px rgba(0,0,0,0.8);overflow-wrap:anywhere;';
    const who = document.createElement('span');
    who.style.cssText = 'font-weight:700;color:#9fffe9;margin-right:6px;';
    who.textContent = name;
    const body = document.createElement('span');
    body.style.cssText = 'color:#e8e8ef;';
    body.textContent = text;
    line.appendChild(who);
    line.appendChild(body);
    this.log.appendChild(line);
    while (this.log.children.length > 40) this.log.removeChild(this.log.firstChild!);
    this.log.scrollTop = this.log.scrollHeight;
    // Cerminkan ke feed pasif (maks 5 terakhir, selalu terlihat saat minimized).
    const feedLine = document.createElement('div');
    feedLine.style.cssText = FEED_LINE;
    const feedWho = document.createElement('span');
    feedWho.style.cssText = 'font-weight:700;color:#9fffe9;margin-right:6px;';
    feedWho.textContent = name;
    const feedBody = document.createElement('span');
    feedBody.style.cssText = 'color:#e8e8ef;';
    feedBody.textContent = text;
    feedLine.appendChild(feedWho);
    feedLine.appendChild(feedBody);
    this.feed.appendChild(feedLine);
    while (this.feed.children.length > FEED_MAX) this.feed.removeChild(this.feed.firstChild!);
    if (!this.expanded) {
      this.unread++;
      this.renderPill();
    }
  }

  isOpen(): boolean {
    return this.expanded;
  }

  /** Dikenal MainGameScene: Enter meng-expand dari pil. */
  open(): void {
    this.setExpanded(true);
  }

  /** Dikenal MainGameScene: Escape/close → minimize. */
  close(): void {
    this.setExpanded(false);
  }

  dispose(): void {
    this.panel.remove();
    this.pill.remove();
    this.feed.remove();
  }

  /** Pasang/copot behavior keyboard; parent memasang global keydown. */
  bindKeys(): void {
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.submit();
      } else if (e.key === 'Escape') {
        this.setExpanded(false);
      }
    });
  }
}